import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import './index.css'

interface OnboardingStep {
	title: string;
	description: string;
	position: 'top' | 'bottom' | 'left' | 'right' | 'center';
	highlightType?: 'element' | 'none'; // 'none' for full-screen info steps
}

interface OnboardingTooltipProps {
	step: number;
	targetRef: React.RefObject<HTMLElement>;
	onNext: () => void;
	onSkip: () => void;
	totalSteps: number;
	isMobile?: boolean;
}

const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
	step,
	targetRef,
	onNext,
	onSkip,
	totalSteps,
	isMobile = false
}) => {
	const { t } = useTranslation('planner')
	const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
	const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({})
	const [showHighlight, setShowHighlight] = useState(true)

	// Generate steps with translations
	const desktopSteps: OnboardingStep[] = useMemo(() => [
		{
			title: t('onboarding.desktop.step1.title'),
			description: t('onboarding.desktop.step1.description'),
			position: 'right',
			highlightType: 'element'
		},
		{
			title: t('onboarding.desktop.step2.title'),
			description: t('onboarding.desktop.step2.description'),
			position: 'bottom',
			highlightType: 'element'
		},
		{
			title: t('onboarding.desktop.step3.title'),
			description: t('onboarding.desktop.step3.description'),
			position: 'left',
			highlightType: 'element'
		},
		{
			title: t('onboarding.desktop.step4.title'),
			description: t('onboarding.desktop.step4.description'),
			position: 'right',
			highlightType: 'element'
		}
	], [t])

	const mobileSteps: OnboardingStep[] = useMemo(() => [
		{
			title: t('onboarding.mobile.step1.title'),
			description: t('onboarding.mobile.step1.description'),
			position: 'center',
			highlightType: 'none'
		},
		{
			title: t('onboarding.mobile.step2.title'),
			description: t('onboarding.mobile.step2.description'),
			position: 'top',
			highlightType: 'element'
		},
		{
			title: t('onboarding.mobile.step3.title'),
			description: t('onboarding.mobile.step3.description'),
			position: 'center',
			highlightType: 'none'
		},
		{
			title: t('onboarding.mobile.step4.title'),
			description: t('onboarding.mobile.step4.description'),
			position: 'center',
			highlightType: 'none'
		}
	], [t])

	// Use appropriate steps based on device
	const steps = isMobile ? mobileSteps : desktopSteps
	const currentStep = steps[step - 1]

	const calculatePosition = useCallback(() => {
		if (!currentStep) return

		const screenWidth = window.innerWidth
		const screenHeight = window.innerHeight
		const padding = isMobile ? 10 : 14
		const tooltipOffset = 20

		// For center position or no highlight, don't need target ref
		if (currentStep.position === 'center' || currentStep.highlightType === 'none') {
			setShowHighlight(false)
			setTooltipStyle({
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)'
			})
			return
		}

		// Need target ref for element highlighting
		if (!targetRef.current) {
			setShowHighlight(false)
			setTooltipStyle({
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)'
			})
			return
		}

		const rect = targetRef.current.getBoundingClientRect()
		setShowHighlight(true)

		// Highlight style - position over the target element
		setHighlightStyle({
			top: rect.top - padding,
			left: rect.left - padding,
			width: rect.width + padding * 2,
			height: rect.height + padding * 2
		})

		// Calculate tooltip position
		let tooltipTop = 0
		let tooltipLeft = 0
		let transform = ''

		if (isMobile) {
			// Mobile: center horizontally, position above or below element
			tooltipLeft = screenWidth / 2
			transform = 'translateX(-50%)'

			if (currentStep.position === 'top') {
				tooltipTop = Math.max(20, rect.top - tooltipOffset)
				transform = 'translate(-50%, -100%)'
			} else {
				// Default to below
				tooltipTop = Math.min(screenHeight - 200, rect.bottom + tooltipOffset)
			}
		} else {
			// Desktop: position based on step configuration
			switch (currentStep.position) {
				case 'right':
					tooltipTop = rect.top + rect.height / 2
					tooltipLeft = Math.min(screenWidth - 340, rect.right + tooltipOffset)
					transform = 'translateY(-50%)'
					break
				case 'bottom':
					tooltipTop = rect.bottom + tooltipOffset
					tooltipLeft = rect.left + rect.width / 2
					transform = 'translateX(-50%)'
					break
				case 'left':
					tooltipTop = rect.top + rect.height / 2
					tooltipLeft = Math.max(20, rect.left - tooltipOffset)
					transform = 'translate(-100%, -50%)'
					break
				case 'top':
					tooltipTop = Math.max(20, rect.top - tooltipOffset)
					tooltipLeft = rect.left + rect.width / 2
					transform = 'translate(-50%, -100%)'
					break
			}
		}

		setTooltipStyle({
			top: tooltipTop,
			left: tooltipLeft,
			transform
		})
	}, [targetRef, currentStep, isMobile])

	useEffect(() => {
		// Small delay to ensure DOM is ready
		const timeoutId = setTimeout(calculatePosition, 100)

		// Recalculate on resize or scroll
		window.addEventListener('resize', calculatePosition)
		window.addEventListener('scroll', calculatePosition, true)

		return () => {
			clearTimeout(timeoutId)
			window.removeEventListener('resize', calculatePosition)
			window.removeEventListener('scroll', calculatePosition, true)
		}
	}, [calculatePosition])

	if (!currentStep || step < 1 || step > totalSteps) {
		return null
	}

	const isLastStep = step === totalSteps
	const isCentered = currentStep.position === 'center' || currentStep.highlightType === 'none'

	return (
		<div className={`onboarding-overlay ${isMobile ? 'mobile' : ''}`}>
			{/* Highlight box around target - only show for element highlights */}
			{showHighlight && currentStep.highlightType === 'element' && (
				<div className='onboarding-highlight' style={highlightStyle} />
			)}

			{/* Tooltip */}
			<div
				className={`onboarding-tooltip ${isCentered ? 'centered' : `position-${currentStep.position}`} ${isMobile ? 'mobile' : ''}`}
				style={tooltipStyle}
			>
				<div className='onboarding-tooltip-content'>
					<div className='onboarding-step-indicator'>
						{step}/{totalSteps}
					</div>
					<h4 className='onboarding-title'>{currentStep.title}</h4>
					<p className='onboarding-description'>{currentStep.description}</p>

					<div className='onboarding-actions'>
						<button
							className='onboarding-skip-btn'
							onClick={onSkip}
						>
							{t('onboarding.skip')}
						</button>
						<button
							className='onboarding-next-btn'
							onClick={onNext}
						>
							{isLastStep ? t('onboarding.finish') : t('onboarding.next')}
						</button>
					</div>
				</div>

				{/* Arrow pointing to target - only for non-centered desktop tooltips */}
				{!isMobile && !isCentered && showHighlight && (
					<div className='onboarding-arrow' />
				)}
			</div>
		</div>
	)
}

export default OnboardingTooltip
